import {
  AlertTriangle,
  Anchor,
  Building2,
  Container as ContainerIcon,
  Factory,
  FileCheck2,
  Map as MapIcon,
  Milestone,
  Package,
  PackagePlus,
  Route,
  Scale,
  Ship,
  Signpost,
  Stamp,
  StickyNote,
  Truck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { RouteMap } from "../RouteMap/RouteMap";
import { routeMapFingerprint } from "../../utils/routeMapPoints";
import type { RouteMeasure } from "../RouteMap/RouteMap";
import { useRouteMapStops } from "../RouteMap/useRouteMapStops";
import { SectionCard } from "../UI/SectionCard";
import { Field, FieldGrid, ReadonlyStopCard } from "./readonlyFields";
import { EMPTY, formatDate, formatDateTime } from "./readonlyFormat";
import { CONTAINER_TYPE_OPTIONS } from "../../constants/containerOrderConstants";
import { IMO_CLASS_OPTIONS } from "../../constants/shipmentFormConstants";
import { effectiveCargoKg } from "../../utils/containerLineCargo";
import { routeStopsOf } from "../../utils/containerLineSteps";
import { STOP_SLOT_KEYS } from "../../utils/containerRoute";
import type {
  ContainerLineStop,
  ContainerOrderLine,
  ContainerStopKind,
  ContainerStopRole,
} from "../../types/containerOrders";
import { vehicleLabel } from "../../utils/vehicleLabel";

/** How each part of the route presents — the same icons the editable card uses. */
const ROLE_PRESENTATION: Record<
  ContainerStopRole,
  { icon: typeof Building2; accent: string; kindKey: string }
> = {
  terminalPickup: {
    icon: Building2,
    accent: "bg-ink",
    kindKey: "route.kindTerminal",
  },
  loadingSite: {
    icon: Factory,
    accent: "bg-copper",
    kindKey: "route.kindClient",
  },
  terminalDropoff: {
    icon: Anchor,
    accent: "bg-ink",
    kindKey: "route.kindTerminal",
  },
  custom: {
    icon: Signpost,
    accent: "bg-amber-600",
    kindKey: "route.kindStop",
  },
};

/** The icon an added stop wears, by what it is for. */
const KIND_ICON: Record<ContainerStopKind, typeof Building2> = {
  customs: FileCheck2,
  weighing: Scale,
  extraLoading: PackagePlus,
  waypoint: Signpost,
  other: Signpost,
};

/**
 * How far this container's run is.
 *
 * Measured over the stops the container actually makes, from the location
 * Google Places saved on each — so it is a figure to read, never a field to
 * fill in, and it appears in read mode only. Says when it is an estimate rather
 * than a road distance, and says so when a used stop has no location instead of
 * quietly reporting a short run.
 *
 * ── Two measurements, one figure ─────────────────────────────────────────────
 *
 * The server measures this, and falls back to straight lines between the stops
 * unless it has a Maps key of its own (`GOOGLE_MAPS_SERVER_KEY`). The map below
 * measures it too, in the browser, with the key the address picker already
 * uses — and on a deployment with only the browser key those two answers are
 * genuinely different numbers for one run: straight-line kilometres in the
 * badge, road kilometres on the legs under it. A card cannot show both and call
 * them the same thing.
 *
 * So the road answer wins when there is one, whichever side produced it. The
 * server's figure is preferred while it *is* a road distance — it is the one
 * every other screen shows and the one that is cached — and the map's road
 * total replaces it only where the server could do no better than a straight
 * line. The legs then add up to the badge, because they are the same
 * measurement.
 *
 * ── Why it waits ─────────────────────────────────────────────────────────────
 *
 * The map's answer arrives a moment after the page does, and showing the
 * server's estimate in the meantime made the badge change its mind in front of
 * the reader: 1,154 km on load, 1,452 km a second later. A figure that settles
 * while you are reading it is worse than a figure that takes a second to
 * appear, because the first one was an assertion and it was wrong.
 *
 * So the badge holds until the answer can no longer improve — and only then.
 * Where the server already measured a *road* distance the map cannot beat it,
 * so there is nothing to wait for and nothing is held back; a deployment with
 * `GOOGLE_MAPS_SERVER_KEY` set never sees the pause at all. Where the map
 * reports that it has nothing to offer, the stored figure is final and shows
 * at once.
 */
function RouteMileage({
  distance,
  measured,
  settled,
}: {
  distance: ContainerOrderLine["routeDistance"];
  /** What the map worked out, or null where it had nothing to measure. */
  measured: RouteMeasure | null;
  /** True once the map has reported, either way. */
  settled: boolean;
}) {
  const { t } = useTranslation(["containerOrders"]);

  const stored =
    distance && distance.km !== null
      ? { km: distance.km, source: distance.source }
      : null;

  // Nothing the map says can improve on a road distance the server measured.
  const pending = !settled && stored?.source !== "road";

  const fromMap =
    measured?.source === "road" && stored?.source !== "road"
      ? { km: Math.round(measured.km * 10) / 10, source: "road" as const }
      : null;

  const figure = fromMap ?? stored;

  if (pending) {
    return (
      <span
        title={t("lineRoute.mileageMeasuring")}
        className="inline-flex animate-pulse items-center gap-1.5 rounded-full border border-gray-200 bg-bone px-2.5 py-1 text-[11.5px] font-semibold"
      >
        <Route className="h-3.5 w-3.5 shrink-0 text-mist" aria-hidden />
        <span className="text-mist">{t("lineRoute.mileage")}</span>
        <span className="tabular-nums text-secondary-300">—</span>
      </span>
    );
  }

  if (!figure) return null;

  const estimate = figure.source === "straight";
  // Stops with no location are the server's count either way: the map draws the
  // same located stops, so a gap in one is a gap in the other.
  const incomplete = (distance?.missingStops ?? 0) > 0;

  return (
    <span
      title={
        estimate
          ? t("lineRoute.mileageStraight")
          : t("lineRoute.mileageRoad")
      }
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-semibold ${
        incomplete
          ? "border-amber-300 bg-amber-50 text-amber-900"
          : "border-gray-200 bg-bone text-ink"
      }`}
    >
      <Route className="h-3.5 w-3.5 shrink-0 text-mist" aria-hidden />
      <span className="text-mist">{t("lineRoute.mileage")}</span>
      <span className="tabular-nums">
        {estimate ? "\u2248 " : ""}
        {figure.km.toLocaleString()} km
      </span>
      {incomplete && (
        <span
          title={t("lineRoute.mileageMissing", {
            count: distance?.missingStops ?? 0,
          })}
          className="inline-flex items-center gap-1"
        >
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
        </span>
      )}
    </span>
  );
}

interface ContainerLineReadonlyProps {
  line: ContainerOrderLine;
}

const kg = (value?: number | null) =>
  typeof value === "number" ? `${value.toLocaleString()} kg` : undefined;

const named = (
  ref: { firstName?: string; lastName?: string } | string | null | undefined,
) =>
  ref && typeof ref === "object"
    ? `${ref.firstName ?? ""} ${ref.lastName ?? ""}`.trim()
    : "";

/**
 * A saved container, laid out for reading.
 *
 * The same argument as the order's read view, and the same primitives: most
 * visits to a container are to *check* something — where it is up to, which box,
 * whose truck, when it is due at the terminal — and a page of live inputs makes
 * that a hunt through boxes, as well as inviting an accidental edit to a record
 * a driver is working from. Editing is a mode you ask for.
 *
 * The order block at the top is deliberately not here: it is identical in both
 * modes and the page renders it once, above whichever half is showing.
 */
export function ContainerLineReadonly({ line }: ContainerLineReadonlyProps) {
  const { t } = useTranslation(["containerOrders", "shipments"]);

  /**
   * The run as points on a map — the same located stops, filtered by the same
   * rules, that the server measures the mileage over. Same stops, note, not
   * necessarily the same kilometres: see `RouteMileage` for which of the two
   * measurements the badge ends up showing.
   */
  const routeMap = useRouteMapStops(line);

  /**
   * What the map made of the run — how it drew it, and how far it came to.
   *
   * Asked of the map rather than read off `routeDistance`, because the two are
   * measured on different sides with different keys and can legitimately
   * disagree. `RouteMileage` decides which of the two the card shows; the note
   * under the map is about the line itself.
   */
  const [measured, setMeasured] = useState<RouteMeasure | null>(null);
  /**
   * Whether the map has reported at all yet — see `RouteMileage` for why the
   * badge waits on it rather than showing a figure it may have to take back.
   */
  const [settled, setSettled] = useState(false);

  const onMeasured = useCallback((measure: RouteMeasure | null) => {
    setMeasured(measure);
    setSettled(true);
  }, []);

  /**
   * A changed route is an unmeasured route.
   *
   * Keyed on the points rather than on the container object, so an ordinary
   * re-render costs nothing — but a stop that moves, or is added, or is turned
   * off puts the badge back to waiting instead of leaving yesterday's figure
   * under today's map.
   */
  const routeKey = useMemo(
    () => routeMapFingerprint(routeMap.stops),
    [routeMap.stops],
  );

  useEffect(() => {
    setMeasured(null);
    setSettled(false);
  }, [routeKey]);

  const containerTypeLabel =
    CONTAINER_TYPE_OPTIONS.find((o) => o.value === line.containerType)?.label ??
    line.containerType;

  /**
   * What is inside, as the container states it.
   *
   * The declared weight when the load has been on a bridge, the packing list's
   * own sum when it has not — the same choice `cargoKgOf` makes on the server
   * and the form makes while it is being typed, so all three read one container
   * the same way.
   */
  const cargoKg = effectiveCargoKg(
    line.cargoWeightKg ?? null,
    (line.cargoItems ?? []).reduce(
      (total, item) => total + (item.weightKg ?? 0),
      0,
    ),
  );

  /**
   * All three handling flags, on or off, and what each one says when it is on.
   *
   * Every one of them is listed rather than only the ones that are set, and
   * that is the point: "this container is not ADR" is an answer, and an absent
   * line is not — it reads as a screen that has not been filled in, or as a
   * feature that is not there. Someone checking whether a box is safe to load
   * needs to see that the question was asked.
   *
   * ADR leads, and is coloured apart from the other two, because it is the one
   * with the law behind it.
   */
  const handlingFlags = [
    {
      key: "adr",
      on: !!line.handling?.adr,
      label: t("handling.adr"),
      // The catalogue's own label ("Class 3 - Flammable Liquids"), which is
      // what the picker offered — a bare "Class3" would be the stored value
      // rather than the thing anyone chose.
      detailLabel: t("labels.imoClass"),
      detail:
        IMO_CLASS_OPTIONS.find((o) => o.value === line.handling?.imoClass)
          ?.label ??
        line.handling?.imoClass ??
        "",
      tone: "bg-[#FBE9E7] text-[#A63A39]",
    },
    {
      key: "temperatureSensitive",
      on: !!line.handling?.temperatureSensitive,
      label: t("handling.temperature"),
      detailLabel: t("labels.tempRange"),
      detail: line.handling?.temperatureRange ?? "",
      tone: "bg-[#E7F0F7] text-[#2C5F82]",
    },
    {
      key: "fragile",
      on: !!line.handling?.fragile,
      label: t("handling.fragile"),
      detailLabel: t("labels.specifications"),
      detail: line.handling?.specifications ?? "",
      tone: "bg-[#FCEEE2] text-[#8A5A28]",
    },
  ];

  const driver = named(line.driverId);
  const plate =
    line.vehicleId && typeof line.vehicleId === "object"
      ? vehicleLabel(line.vehicleId)
      : "";

  /** The four rows every stop shows, in the order the form has them. */
  const stopRows = (
    stop: ContainerLineStop | undefined,
    refLabel: string,
    dateLabel: string,
    timeLabel: string,
  ) => [
    { label: refLabel, value: stop?.bookingRef },
    { label: dateLabel, value: formatDate(stop?.date) },
    { label: timeLabel, value: stop?.time },
    { label: t("route.pin"), value: stop?.pin },
  ];

  /** Where the driver has got to at this stop — absent until they arrive. */
  const progressRows = (stop?: ContainerLineStop) =>
    [
      stop?.arrivedAt
        ? {
            label: t("linePage.arrived"),
            value: new Date(stop.arrivedAt).toLocaleString(),
          }
        : null,
      stop?.completedAt
        ? {
            label: t("linePage.departed"),
            value: new Date(stop.completedAt).toLocaleString(),
          }
        : null,
    ].filter(Boolean) as Array<{ label: string; value?: React.ReactNode }>;

  return (
    <div className="space-y-3">
      {/* ── How far the run has got ──────────────────────────────────────
          Above everything else, in the same place and under the same heading
          the edit form gives it, because a dispatcher switching between the two
          modes should not have to go looking for the one field they came to
          check. Until now it was on this page only as the pill in the header,
          which the timeline rail can cover and which says nothing about the
          release: a container generated with its order and one dispatch has
          actually sent out are the same box until somebody reads its status. */}
      <SectionCard icon={Milestone} title={t("lineEdit.sections.progress")}>
        <FieldGrid>
          <Field
            label={t("lineEdit.fields.status")}
            value={t(`linesBlock.statuses.${line.status}`)}
          />
          {/* When the office released it, beside where it has got to. Blank on
              a container nobody has planned, which is the honest answer and
              reads as one — an em dash rather than a date is exactly the
              difference the status above is describing. */}
          <Field
            label={t("lineEdit.fields.plannedAt")}
            value={formatDateTime(line.plannedAt)}
          />
        </FieldGrid>
      </SectionCard>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {/* ── The box ────────────────────────────────────────────────────── */}
        <SectionCard
          icon={ContainerIcon}
          title={t("lineEdit.sections.container")}
        >
          <FieldGrid>
            <Field
              label={t("lineEdit.fields.containerNumber")}
              value={line.containerNumber}
              mono
            />
            <Field label={t("labels.containerType")} value={containerTypeLabel} />
            {/* Under the type, exactly where the form puts it. A container
                nobody has described reads as blank rather than as "empty" —
                the two are different answers and only one of them was given. */}
            <Field
              label={t("labels.loadStatus")}
              value={
                line.loadStatus ? t(`loadStatus.${line.loadStatus}`) : undefined
              }
            />
            <Field label={t("labels.isoCode")} value={line.isoCode} mono />
            <Field label={t("lineEdit.fields.tare")} value={kg(line.tareKg)} />
            <Field
              label={t("lineEdit.fields.maxPayload")}
              value={kg(line.maxPayloadKg)}
            />
            {/* What is inside, and where the figure came from. A weighed load
                and a summed packing list are both valid answers and they are
                corrected in different places — the first here, the second in
                the table below — so the label says which one is being read
                rather than leaving them to look identical. */}
            <Field
              label={t(
                line.cargoWeightKg != null
                  ? "lineEdit.fields.cargoWeight"
                  : "lineEdit.fields.listedWeight",
              )}
              value={`${cargoKg.toLocaleString()} kg`}
            />
            <Field label={t("lineEdit.fields.vgm")} value={kg(line.vgmKg)} />
            <Field
              label={t("lineEdit.fields.sealNumber")}
              value={line.sealNumber}
              mono
            />
          </FieldGrid>

        </SectionCard>

        <div className="space-y-3">
          {/* ── Who is driving it ──────────────────────────────────────── */}
          <SectionCard icon={Truck} title={t("lineEdit.sections.assignment")}>
            <FieldGrid>
              <Field label={t("lineEdit.fields.driver")} value={driver} />
              <Field label={t("lineEdit.fields.vehicle")} value={plate} />
            </FieldGrid>
          </SectionCard>

          {/* ── The sailing ────────────────────────────────────────────── */}
          <SectionCard icon={Ship} title={t("lineEdit.sections.booking")}>
            <FieldGrid>
              <Field
                label={t("labels.shippingLine")}
                value={line.shippingLine}
              />
              <Field
                label={t("labels.vesselVoyage")}
                value={
                  [line.vesselName, line.voyageNumber]
                    .filter(Boolean)
                    .join(" / ") || undefined
                }
              />
              <Field label={t("labels.blNumber")} value={line.blNumber} mono />
              <Field
                label={t("lineEdit.fields.bookingRef")}
                value={line.bookingRef}
                mono
              />
            </FieldGrid>
          </SectionCard>

          {/* ── Customs ──────────────────────────────────────────────────
              Its own card here for the same reason it is one on the form: an
              MRN answers about the goods and comes from a national customs
              system, while everything above it describes the sailing and comes
              from the carrier. Presenting a thing where it is edited is what
              stops a reader hunting for it twice. */}
          <SectionCard icon={Stamp} title={t("lineEdit.sections.customs")}>
            <FieldGrid>
              <Field
                label={t("lineEdit.fields.mrnNumber")}
                value={line.mrnNumber}
                mono
              />
            </FieldGrid>
          </SectionCard>

          {/* ── How this box has to be handled ────────────────────────────────
              Its own card, under the booking, matching where the form puts it —
              a page that presents a thing in one place and edits it in another
              makes the reader hunt for it twice.

              All three flags, always, set or not. An unset flag shown as "No" is
              an answer; an unset flag shown as nothing is indistinguishable from
              a screen that forgot to ask. Where a flag is set, what it says is
              printed beside it — an IMO class or a temperature range is the
              whole reason the flag is worth having. */}
          <SectionCard
            icon={AlertTriangle}
            title={t("lineEdit.sections.handling")}
          >
            <div className="space-y-1.5">
              {handlingFlags.map(({ key, on, label, detailLabel, detail, tone }) => (
                <div key={key} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span
                    className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${
                      on ? tone : "bg-secondary-100 text-secondary-500"
                    }`}
                  >
                    {on && <AlertTriangle className="h-3 w-3" />}
                    {label}
                    <span className={on ? "" : "font-normal"}>
                      · {t(on ? "readonly.yes" : "readonly.no")}
                    </span>
                  </span>

                  {/* Only where the flag is on: a detail under a "No" would be
                      a value with nothing to explain it. Shown as a blank rather
                      than omitted when the flag is on and nobody filled it in —
                      that is a gap someone should notice. */}
                  {on && (
                    <span className="text-[12px] text-mist">
                      {detailLabel}:{" "}
                      <span className="text-ink">{detail || EMPTY}</span>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>

      {/* ── The run ──────────────────────────────────────────────────────────
          Every stop this container makes, in the order it makes them — read off
          its own route rather than off the three anchors. That is the whole
          point of reading it here: a box that goes by way of a weighbridge, or
          one that is not being collected from a terminal at all, is a container
          whose run differs from its batch's, and this is where anyone checking
          it would look. */}
      <SectionCard
        icon={MapIcon}
        title={t("sections.routeTerminals")}
        /* The run's length, beside the run itself — read-only, because it is
           measured from the stops below rather than typed. */
        actions={
          <RouteMileage
            distance={line.routeDistance}
            measured={measured}
            settled={settled}
          />
        }
      >
        {/* The run on the ground, above the run as records. The numbered pins
            are the numbers on the cards below, which is what lets someone read
            "the weighbridge is a detour" off the map and then go and find out
            when it is booked for. */}
        <RouteMap
          stops={routeMap.stops}
          titles={routeMap.titles}
          className="mb-3 h-72 @2xl:h-80"
          onMeasured={onMeasured}
        />
        {measured?.source === "straight" && (
          <p className="mb-3 text-[11.5px] text-mist">
            {t("lineRoute.mapStraight")}
          </p>
        )}
        {/* A map drawn over fewer stops than the container makes has to say so
            — the same thing the mileage badge says about the figure. */}
        {routeMap.missing > 0 && (
          <p className="mb-3 text-[11.5px] text-amber-800">
            {t("lineRoute.mapMissing", { count: routeMap.missing })}
          </p>
        )}

        <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-3">
          {routeStopsOf(line).map((stop, index) => {
            const custom = stop.role === "custom";
            const used = stop.enabled !== false;
            const slots = STOP_SLOT_KEYS[stop.role] ?? STOP_SLOT_KEYS.custom;
            const kindOf = ROLE_PRESENTATION[stop.role] ?? ROLE_PRESENTATION.custom;
            const Icon = custom
              ? (KIND_ICON[stop.kind ?? "other"] ?? Signpost)
              : kindOf.icon;

            return (
              <ReadonlyStopCard
                key={String(stop._id)}
                index={index + 1}
                icon={Icon}
                title={
                  custom
                    ? stop.label?.trim() ||
                      t(`route.stopKinds.${stop.kind ?? "other"}`)
                    : t(`route.${stop.role}`)
                }
                kind={
                  custom
                    ? t(`route.stopKinds.${stop.kind ?? "other"}`)
                    : t(kindOf.kindKey)
                }
                accent={used ? kindOf.accent : "bg-secondary-300"}
                muted={!used}
                companyName={stop.companyName}
                address={stop.address}
                rows={
                  used
                    ? [
                        ...stopRows(
                          stop,
                          t(slots.ref),
                          t(slots.date),
                          t(slots.time),
                        ),
                        // Only where there is something to say: a terminal gate
                        // has no ramp and nobody to ask for.
                        ...(stop.ramp
                          ? [{ label: t("route.ramp"), value: stop.ramp }]
                          : []),
                        ...(stop.contactName || stop.contactPhone
                          ? [
                              {
                                label: t("route.contact"),
                                value: [stop.contactName, stop.contactPhone]
                                  .filter(Boolean)
                                  .join(" · "),
                              },
                            ]
                          : []),
                        ...(stop.comment
                          ? [
                              {
                                label: t("route.stopComment"),
                                value: stop.comment,
                              },
                            ]
                          : []),
                        // The cut-off reads with the terminal that enforces it,
                        // exactly as it is entered.
                        ...(stop.role === "terminalDropoff"
                          ? [
                              {
                                label: t("route.cutOffDate"),
                                value: formatDate(line.cutOffDate),
                              },
                              {
                                label: t("route.cutOffTime"),
                                value: line.cutOffTime,
                              },
                            ]
                          : []),
                        ...progressRows(stop),
                      ]
                    : // A stop that is not being made says one thing, and it is
                      // the only thing worth saying about it.
                      [
                        {
                          label: t("route.skipReason"),
                          value: stop.skipReason || EMPTY,
                        },
                      ]
                }
              />
            );
          })}
        </div>

        {/* The cut-off with nowhere to live, because the box is not going back
            to a terminal. Still shown: it is the deadline the whole run answers
            to, and the batch list has a column for it. */}
        {!routeStopsOf(line).some(
          (stop) => stop.role === "terminalDropoff" && stop.enabled !== false,
        ) &&
          (line.cutOffDate || line.cutOffTime) && (
            <div className="mt-3 border-t border-[rgba(11,15,14,0.06)] pt-3">
              <FieldGrid>
                <Field
                  label={t("route.cutOffDate")}
                  value={formatDate(line.cutOffDate)}
                />
                <Field label={t("route.cutOffTime")} value={line.cutOffTime} />
              </FieldGrid>
            </div>
          )}
      </SectionCard>

      {/* ── What is inside ───────────────────────────────────────────────── */}
      <SectionCard icon={Package} title={t("lineCargo.title")}>
        {line.cargoItems?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-xl border-collapse">
              <thead>
                <tr className="border-b border-[rgba(11,15,14,0.08)]">
                  {[
                    t("lineCargo.columns.qty"),
                    t("lineCargo.columns.type"),
                    t("lineCargo.columns.description"),
                    t("lineCargo.columns.weightKg"),
                    t("lineCargo.columns.volumeM3"),
                    t("lineCargo.columns.marks"),
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-mist"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {line.cargoItems.map((item, index) => (
                  <tr
                    key={item._id ?? index}
                    className="border-b border-[rgba(11,15,14,0.05)] last:border-0"
                  >
                    <td className="px-2 py-1.5 text-[13px] text-ink">
                      {item.quantity ?? EMPTY}
                    </td>
                    <td className="px-2 py-1.5 text-[13px] text-ink">
                      {item.type || EMPTY}
                    </td>
                    <td className="px-2 py-1.5 text-[13px] text-ink">
                      {item.description || EMPTY}
                    </td>
                    <td className="px-2 py-1.5 text-[13px] text-ink">
                      {kg(item.weightKg) ?? EMPTY}
                    </td>
                    <td className="px-2 py-1.5 text-[13px] text-ink">
                      {item.volumeM3 != null ? `${item.volumeM3} m³` : EMPTY}
                    </td>
                    <td className="px-2 py-1.5 text-[13px] text-ink">
                      {item.marks || EMPTY}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-[13px] text-secondary-300">
            {t("lineCargo.emptyReadOnly")}
          </p>
        )}
      </SectionCard>

      <SectionCard icon={StickyNote} title={t("lineEdit.sections.notes")}>
        <p
          className={`whitespace-pre-wrap text-[13px] ${
            line.notes ? "text-ink" : "text-secondary-300"
          }`}
        >
          {line.notes || EMPTY}
        </p>
      </SectionCard>
    </div>
  );
}
